import { useEffect, useState } from "react";
import { api, formatErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Package, CurrencyDollar, TrendUp, Eye, Upload, X } from "@phosphor-icons/react";
import PayoutsTab from "@/pages/PayoutsTab";
import ShippingTab from "@/pages/ShippingTab";
import OrderDetailsModal from "@/components/OrderDetailsModal";

const CATEGORIES = ["hoodie", "tshirt", "cap", "limited", "other"];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-white/10 p-6 bg-[#0A0A0A]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">{label}</div>
          <div className="font-display text-4xl mt-2">{value}</div>
          {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
        </div>
        <Icon size={24} weight="bold" className="text-[#FF3B00]" />
      </div>
    </div>
  );
}

function ProfileTab({ user, refresh }) {
  const [form, setForm] = useState({
    artist_name: user?.artist_name || user?.name || "",
    bio: user?.bio || "",
    avatar_url: user?.avatar_url || "",
    social_instagram: user?.social_instagram || "",
    social_twitter: user?.social_twitter || "",
    social_website: user?.social_website || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("avatar_url", fileUrl(data.path));
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setUploading(false); }
  };

  const removeAvatar = () => upd("avatar_url", "");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/artists/profile", form);
      toast.success("Profil mis à jour");
      refresh && refresh();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="profile-tab">
      <div>
        <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Photo de profil</label>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 bg-[#111] border border-white/10 overflow-hidden flex items-center justify-center font-display text-3xl flex-shrink-0">
            {form.avatar_url ? (
              <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              (form.artist_name || "?")[0]?.toUpperCase()
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-[#FF3B00] hover:text-[#FF3B00] uppercase tracking-widest text-xs cursor-pointer">
              <Upload size={14} />
              {uploading ? "Envoi..." : (form.avatar_url ? "Changer la photo" : "Téléverser une photo")}
              <input type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-input" />
            </label>
            {form.avatar_url && (
              <button type="button" onClick={removeAvatar} className="text-xs uppercase tracking-widest text-red-500 hover:underline text-left">
                Retirer la photo
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Surnom du pilote / écurie</label>
        <input
          data-testid="profile-name"
          value={form.artist_name}
          onChange={(e) => upd("artist_name", e.target.value)}
          className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3"
          placeholder="ex : Guillaume Larose Racing"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Bio</label>
        <textarea
          data-testid="profile-bio"
          value={form.bio}
          onChange={(e) => upd("bio", e.target.value)}
          rows={4}
          className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3 resize-none"
          placeholder="Discipline, palmarès, équipe... (max 500 caractères)"
          maxLength={500}
        />
        <div className="text-xs text-white/40 mt-1 text-right">{form.bio.length} / 500</div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Instagram</label>
          <input
            value={form.social_instagram}
            onChange={(e) => upd("social_instagram", e.target.value)}
            className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3"
            placeholder="@ton_compte"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60 block mb-2">Site web</label>
          <input
            value={form.social_website}
            onChange={(e) => upd("social_website", e.target.value)}
            className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3"
            placeholder="https://..."
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="profile-save"
        className="px-8 py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50"
      >
        {saving ? "Enregistrement..." : "Enregistrer le profil"}
      </button>
    </form>
  );
}

function ProductForm({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product || {
    name: "", description: "", price: 0, category: "hoodie",
    images: [], variants: [{ size: "M", color: "", stock: 10 }], stock: 10, limited_edition: false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("images", [...(form.images || []), data.path]);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setUploading(false); }
  };

  const removeImage = (i) => upd("images", form.images.filter((_, idx) => idx !== i));

  const addVariant = () => upd("variants", [...(form.variants || []), { size: "", color: "", stock: 0 }]);
  const updVariant = (i, k, v) => upd("variants", form.variants.map((va, idx) => idx === i ? { ...va, [k]: v } : va));
  const rmVariant = (i) => upd("variants", form.variants.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock || 0) };
      payload.variants = (payload.variants || []).map((v) => ({ ...v, stock: parseInt(v.stock || 0) }));
      if (product?.id) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produit enregistré");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#0A0A0A] border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form">
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0A0A0A]">
          <div className="font-display text-3xl uppercase">{product ? "Modifier" : "Nouveau produit"}</div>
          <button type="button" onClick={onClose}><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Nom</label>
            <input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="prod-name"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Prix ($)</label>
              <input type="number" step="0.01" required value={form.price} onChange={(e) => upd("price", e.target.value)} data-testid="prod-price"
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Catégorie</label>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)}
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Stock total</label>
              <input type="number" value={form.stock} onChange={(e) => upd("stock", e.target.value)}
                className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.limited_edition} onChange={(e) => upd("limited_edition", e.target.checked)} />
            Édition limitée
          </label>

          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Photos</div>
            <div className="flex flex-wrap gap-2">
              {(form.images || []).map((im, i) => (
                <div key={i} className="relative w-20 h-20 border border-white/20">
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-[#FF3B00] text-white w-5 h-5 flex items-center justify-center text-xs"><X size={12} /></button>
                </div>
              ))}
              <label className="w-20 h-20 border border-dashed border-white/30 hover:border-[#FF3B00] flex items-center justify-center cursor-pointer">
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="prod-upload" />
                {uploading ? "..." : <Upload size={20} />}
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-white/60">Variations (taille / couleur)</div>
              <button type="button" onClick={addVariant} className="text-xs uppercase tracking-widest text-[#FF3B00] hover:underline">+ Ajouter</button>
            </div>
            <div className="space-y-2">
              {(form.variants || []).map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_80px_40px] gap-2">
                  <input placeholder="Taille (S, M, ...)" value={v.size} onChange={(e) => updVariant(i, "size", e.target.value)}
                    className="bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
                  <input placeholder="Couleur" value={v.color} onChange={(e) => updVariant(i, "color", e.target.value)}
                    className="bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
                  <input type="number" placeholder="Stock" value={v.stock} onChange={(e) => updVariant(i, "stock", e.target.value)}
                    className="bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
                  <button type="button" onClick={() => rmVariant(i)} className="text-white/40 hover:text-[#FF3B00]"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-white/10 sticky bottom-0 bg-[#0A0A0A] flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 border border-white/20 hover:border-white uppercase tracking-widest">Annuler</button>
          <button type="submit" disabled={saving} data-testid="prod-save" className="flex-1 py-3 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ArtistDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);

  const fetchAll = async () => {
    const [s, p, o] = await Promise.all([
      api.get("/artists/me/stats").then(r => r.data).catch(() => null),
      api.get("/artists/me/products").then(r => r.data).catch(() => []),
      api.get("/orders/artist").then(r => r.data).catch(() => []),
    ]);
    setStats(s); setProducts(p); setOrders(o);
  };

  useEffect(() => { fetchAll(); }, []);

  const markShipped = async (id) => {
    try {
      await api.post(`/orders/${id}/ship`);
      toast.success("Marqué comme expédié");
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async (id) => {
    if (!confirm("Supprimer ce produit ?")) return;
    try { await api.delete(`/products/${id}`); fetchAll(); toast.success("Supprimé"); } catch (err) { toast.error(formatErr(err)); }
  };

  const isPending = user?.status === "pending";

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="artist-dashboard">
      <div className="border-b border-white/10 pb-6 mb-8 flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Studio pilote</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-1">{user?.artist_name || user?.name}</h1>
        </div>
        {isPending && (
          <div className="border border-yellow-500 text-yellow-500 px-4 py-2 text-xs uppercase tracking-widest">Compte en attente de validation</div>
        )}
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
        {[
          { k: "overview", label: "Vue d'ensemble" },
          { k: "profile", label: "Profil" },
          { k: "products", label: "Produits" },
          { k: "orders", label: "Commandes" },
          { k: "shipping", label: "Livraison" },
          { k: "payouts", label: "Paiements" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-6 py-3 uppercase tracking-widest text-xs whitespace-nowrap ${tab === t.k ? "border-b-2 border-[#FF3B00] text-white" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CurrencyDollar} label="Revenus totaux" value={`$${stats.total_revenue}`} sub={`Vous recevez $${stats.artist_payout}`} />
          <StatCard icon={Package} label="Ventes" value={stats.total_sales} sub={`${stats.product_count} produits`} />
          <StatCard icon={Eye} label="Vues" value={stats.total_views} />
          <StatCard icon={TrendUp} label="Conversion" value={`${stats.conversion_rate}%`} />
          <div className="lg:col-span-4 border border-white/10 p-6 bg-[#0A0A0A]">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-4">Top produits</div>
            {stats.top_products?.length === 0 ? <div className="text-white/50">Aucune vente pour le moment.</div> :
              <div className="space-y-2">
                {stats.top_products.map((p) => (
                  <div key={p.id} className="flex justify-between items-center py-2 border-b border-white/5">
                    <span>{p.name}</span>
                    <span className="font-display text-xl">{p.sales_count} ventes</span>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      )}

      {tab === "products" && (
        <div>
          <button onClick={() => setCreating(true)} data-testid="new-product-btn" disabled={isPending}
            className="mb-6 px-6 py-3 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] inline-flex items-center gap-2 disabled:opacity-40">
            <Plus size={16} /> Nouveau produit
          </button>
          <div className="border border-white/10">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 bg-[#0A0A0A]">
                <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                  <th className="p-4">Produit</th>
                  <th className="p-4">Prix</th>
                  <th className="p-4">Stock</th>
                  <th className="p-4">Ventes</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="p-4 flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#111]">
                        {p.images?.[0] && <img src={fileUrl(p.images[0])} className="w-full h-full object-cover" alt="" />}
                      </div>
                      <span>{p.name}</span>
                    </td>
                    <td className="p-4">${p.price}</td>
                    <td className="p-4">{p.stock}</td>
                    <td className="p-4">{p.sales_count || 0}</td>
                    <td className="p-4">
                      <button onClick={() => setEditing(p)} className="text-white/60 hover:text-[#FF3B00] mr-3"><Pencil size={16} /></button>
                      <button onClick={() => del(p.id)} className="text-white/60 hover:text-red-500"><X size={16} /></button>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan={5} className="p-16 text-center text-white/50">Aucun produit encore.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#0A0A0A]">
              <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                <th className="p-4">Commande</th>
                <th className="p-4">Client</th>
                <th className="p-4">Produits</th>
                <th className="p-4">Total</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const total = o.items.reduce((s, i) => s + i.price * i.quantity, 0);
                return (
                  <tr key={o.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setViewingOrder(o)} data-testid={`order-row-${o.id}`}>
                    <td className="p-4">
                      <button className="text-[#FF3B00] font-mono hover:underline">#{o.id.slice(-6).toUpperCase()}</button>
                    </td>
                    <td className="p-4">{o.customer_details?.name || o.buyer_email}</td>
                    <td className="p-4">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</td>
                    <td className="p-4">${total.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`text-xs uppercase px-2 py-1 ${o.shipping_status === "shipped" ? "bg-green-500/20 text-green-400" : "bg-[#FF3B00]/20 text-[#FF3B00]"}`}>
                        {o.shipping_status === "shipped" ? "Expédié" : "À préparer"}
                      </span>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      {o.shipping_status !== "shipped" && (
                        <button onClick={() => markShipped(o.id)} data-testid={`ship-${o.id}`}
                          className="text-xs uppercase tracking-widest text-[#FF3B00] hover:underline">Marquer expédié</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr><td colSpan={6} className="p-16 text-center text-white/50">Aucune commande.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "profile" && <ProfileTab user={user} refresh={refresh} />}

      {tab === "payouts" && <PayoutsTab user={user} refresh={refresh} />}

      {tab === "shipping" && <ShippingTab user={user} refresh={refresh} />}

      {viewingOrder && (
        <OrderDetailsModal order={viewingOrder} onClose={() => setViewingOrder(null)} />
      )}

      {(creating || editing) && (
        <ProductForm
          product={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
}
